import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { toast } from 'sonner';
import { UserCheck, CheckCircle2, TrendingUp, Clock, Users, Download, Loader2, ExternalLink, RefreshCw } from 'lucide-react';
import { isMgr, fmtDate, ALIGN_META } from './demoConstants';
import DemoPeriodSelector from './DemoPeriodSelector';
import SearchSelect from './SearchSelect';
import DemoDetailModal from './DemoDetailModal';
import { PageHeader, Stat, EmptyState } from './DemoUI';

const csv = (rows) => {
  const head = ['Demo', 'Demo Date', 'Course', 'Trainer', 'Counsellor', 'Student', 'Enrolment No', 'Admitted On', 'Days to Admission', 'Link Status', 'Alignment'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows.map((r) => [r.demo_code, r.demo_date, r.course_name, r.trainer_name, r.counsellor_name,
    r.student_name, r.enrollment_no, (r.admitted_at || '').slice(0, 10), r.days_to_admission ?? '', r.enrollment_link || '', r.alignment || ''].map(esc).join(','));
  return [head.map(esc).join(','), ...body].join('\n');
};

const CreditTable = ({ rows, head, testid }) => (
  <Card className="border overflow-hidden" data-testid={testid}>
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
          <th className="px-4 py-3">{head}</th><th className="px-4 py-3">Approved Demos</th>
          <th className="px-4 py-3">Admissions</th><th className="px-4 py-3">Conversion</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" data-testid={`credit-row-${r.name}`}>
              <td className="px-4 py-3 font-semibold text-slate-800">{r.name}</td>
              <td className="px-4 py-3 tabular-nums text-slate-600">{r.demos}</td>
              <td className="px-4 py-3 tabular-nums font-semibold text-emerald-700">{r.admissions}</td>
              <td className="px-4 py-3 tabular-nums font-semibold text-violet-700">{r.conversion_pct}%</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-[12px] text-slate-400">No approved demos in this period.</td></tr>}
        </tbody>
      </table>
    </div>
  </Card>
);

export default function DemoConversionsPage({ embedded = false }) {
  const { user } = useAuth();
  const role = user?.role;
  const mgr = isMgr(role);
  const [period, setPeriod] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [masters, setMasters] = useState({ trainers: [], counsellors: [], courses: [] });
  const [f, setF] = useState({ trainer_id: '', counsellor_id: '', course_id: '' });
  const [tab, setTab] = useState('trainer');
  const [openId, setOpenId] = useState(null);
  const [relinking, setRelinking] = useState(false);

  useEffect(() => {
    api.get('/training/demos/masters/all')
      .then((r) => setMasters({ trainers: r.data.trainers || [], counsellors: r.data.counsellors || [], courses: r.data.courses || [] }))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    try {
      const params = { date_from: period.from_date, date_to: period.to_date };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get('/training/demos/conversions', { params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load conversions'); }
    finally { setLoading(false); }
  }, [period, f]);
  useEffect(() => { load(); }, [load]);

  const relink = async () => {
    setRelinking(true);
    try {
      const r = await api.post('/training/demos/conversions/relink');
      toast.success(r.data.linked ? `${r.data.linked} enrolment number(s) linked` : 'No new enrolment numbers found yet');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-check failed'); }
    finally { setRelinking(false); }
  };

  const t = data?.totals || {};
  const rows = data?.rows || [];

  const download = () => {
    const blob = new Blob([csv(rows)], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `demo-conversions-${period?.from_date}_${period?.to_date}.csv`;
    a.click();
  };

  return (
    <div className={embedded ? "space-y-6" : "space-y-8"} data-testid="demo-conversions-page">
      <PageHeader embedded={embedded} icon={UserCheck} eyebrow="Trainer Demo" title="Demo → Admission Conversions"
        subtitle="Students who enrolled after a demo, with trainer and counsellor credit." testid="demo-conversions-header">
        {mgr && (
          <Button variant="outline" size="sm" className="h-10" onClick={relink} disabled={relinking} data-testid="conversions-relink">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${relinking ? 'animate-spin' : ''}`} />Re-check links
          </Button>
        )}
        <Button variant="outline" size="sm" className="h-10" onClick={download} disabled={!rows.length} data-testid="conversions-export">
          <Download className="w-4 h-4 mr-1.5" />Export CSV
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="conversions-kpi-rail">
        <Stat icon={CheckCircle2} label="Admissions" value={t.admissions ?? 0} tone="emerald" testid="ckpi-admissions" />
        <Stat icon={TrendingUp} label="Conversion" value={`${t.conversion_pct ?? 0}%`} tone="violet" sub={`of ${t.approved_demos ?? 0} approved demos`} testid="ckpi-conversion" />
        <Stat icon={Users} label="Unique Students" value={t.unique_students ?? 0} tone="sky" testid="ckpi-students" />
        <Stat icon={Clock} label="Avg Days to Admit" value={t.avg_days_to_admission ?? '—'} tone="amber" sub="demo date → admission" testid="ckpi-days" />
        <Stat icon={Clock} label="Awaiting Link" value={t.awaiting_link ?? 0} tone="rose" sub="enrolment not synced yet" testid="ckpi-awaiting" />
      </div>

      <Card className="p-4 border flex flex-wrap items-center gap-2" data-testid="conversions-filter-bar">
        <DemoPeriodSelector onChange={setPeriod} defaultKey="this_month" />
        {mgr && <SearchSelect value={f.trainer_id} onChange={(v) => setF((p) => ({ ...p, trainer_id: v }))} options={masters.trainers} allowAll allLabel="All trainers" testid="conv-filter-trainer" className="w-44" />}
        {(mgr || role === 'trainer') && <SearchSelect value={f.counsellor_id} onChange={(v) => setF((p) => ({ ...p, counsellor_id: v }))} options={masters.counsellors} allowAll allLabel="All counsellors" testid="conv-filter-counsellor" className="w-44" />}
        <SearchSelect value={f.course_id} onChange={(v) => setF((p) => ({ ...p, course_id: v }))} options={masters.courses} allowAll allLabel="All courses" testid="conv-filter-course" className="w-48" />
        <Button variant="ghost" size="sm" className="h-9 text-[12px] text-slate-500" onClick={() => setF({ trainer_id: '', counsellor_id: '', course_id: '' })} data-testid="conv-clear-filters">Clear</Button>
      </Card>

      <section className="space-y-3" data-testid="credit-section">
        <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">Conversion credit</h2>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100" data-testid="credit-tabs">
            <TabsTrigger value="trainer" className="data-[state=active]:bg-white data-[state=active]:shadow text-xs" data-testid="credit-tab-trainer">By Trainer</TabsTrigger>
            <TabsTrigger value="counsellor" className="data-[state=active]:bg-white data-[state=active]:shadow text-xs" data-testid="credit-tab-counsellor">By Counsellor</TabsTrigger>
          </TabsList>
          <TabsContent value="trainer" className="mt-3"><CreditTable rows={data?.by_trainer || []} head="Trainer" testid="credit-table-trainer" /></TabsContent>
          <TabsContent value="counsellor" className="mt-3"><CreditTable rows={data?.by_counsellor || []} head="Counsellor" testid="credit-table-counsellor" /></TabsContent>
        </Tabs>
      </section>

      {(data?.awaiting_link_rows || []).length > 0 && (
        <section className="space-y-3" data-testid="awaiting-link-section">
          <h2 className="text-[13px] font-semibold text-amber-700 uppercase tracking-wider">
            Awaiting enrolment link <span className="text-amber-500 normal-case">({data.awaiting_link_rows.length})</span>
          </h2>
          <Card className="border border-amber-200 overflow-hidden">
            <p className="px-4 py-2.5 text-[11.5px] text-amber-800 bg-amber-50 border-b border-amber-200">
              These admissions are recorded but their enrolment number has not appeared in the Students master yet. They link automatically once CRM/Books syncs — or hit “Re-check links”.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                  <th className="px-4 py-3">Demo</th><th className="px-4 py-3">Entered Enrolment</th><th className="px-4 py-3">Prospect</th>
                  <th className="px-4 py-3">Course</th><th className="px-4 py-3">Counsellor</th><th className="px-4 py-3">Recorded</th>
                </tr></thead>
                <tbody>
                  {data.awaiting_link_rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-amber-50/60 transition-colors" data-testid={`awaiting-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => setOpenId(r.id)} className="font-mono text-[11.5px] text-violet-700 hover:underline">{r.demo_code}</button>
                        <span className="block text-[10.5px] text-slate-400">{fmtDate(r.demo_date)}</span>
                      </td>
                      <td className="px-4 py-3 font-mono text-[11.5px] text-amber-800">{r.enrollment_no ? `#${r.enrollment_no}` : <span className="font-sans text-amber-600">not issued yet</span>}</td>
                      <td className="px-4 py-3 text-slate-700">{r.student_name || '—'}</td>
                      <td className="px-4 py-3 text-slate-700">{r.course_name}</td>
                      <td className="px-4 py-3 text-slate-700">{r.counsellor_name}</td>
                      <td className="px-4 py-3 text-slate-600">{r.admitted_at ? fmtDate(r.admitted_at.slice(0, 10)) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      )}

      <section className="space-y-3" data-testid="conversions-list-section">
        <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">
          Enrolled after a demo <span className="text-slate-400 normal-case">({rows.length})</span>
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading conversions…</div>
        ) : rows.length === 0 ? (
          <Card className="border"><EmptyState title="No admissions from demos in this period" hint="Record an admission outcome with an enrolment number to see it here." /></Card>
        ) : (
          <Card className="border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
                  <th className="px-4 py-3">Student</th><th className="px-4 py-3">Enrolment</th><th className="px-4 py-3">Demo</th>
                  <th className="px-4 py-3">Course</th><th className="px-4 py-3">Trainer</th><th className="px-4 py-3">Counsellor</th>
                  <th className="px-4 py-3">Admitted</th><th className="px-4 py-3">Days</th><th className="px-4 py-3">Alignment</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-violet-50 transition-colors" data-testid={`conversion-row-${r.id}`}>
                      <td className="px-4 py-3 font-semibold text-slate-800">{r.student_name || '—'}</td>
                      <td className="px-4 py-3">
                        {r.enrollment_link === 'linked' ? (
                          <a href={`/training/students?focus=${r.enrollment_no}`} className="inline-flex items-center gap-1 font-mono text-[11.5px] text-emerald-700 hover:underline" data-testid={`conversion-student-link-${r.id}`}>
                            #{r.enrollment_no}<ExternalLink className="w-3 h-3" />
                          </a>
                        ) : r.enrollment_no ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-amber-700" title="Awaiting CRM/Books sync">
                            <span className="font-mono">#{r.enrollment_no}</span><Clock className="w-3 h-3" />
                          </span>
                        ) : <span className="text-[11px] text-amber-600">awaited</span>}
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => setOpenId(r.id)} className="font-mono text-[11.5px] text-violet-700 hover:underline" data-testid={`conversion-demo-link-${r.id}`}>{r.demo_code}</button>
                        <span className="block text-[10.5px] text-slate-400">{fmtDate(r.demo_date)}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{r.course_name}</td>
                      <td className="px-4 py-3 text-slate-700">{r.trainer_name}</td>
                      <td className="px-4 py-3 text-slate-700">{r.counsellor_name}</td>
                      <td className="px-4 py-3 text-slate-600">{r.admitted_at ? fmtDate(r.admitted_at.slice(0, 10)) : '—'}</td>
                      <td className="px-4 py-3 tabular-nums text-slate-600">{r.days_to_admission ?? '—'}</td>
                      <td className="px-4 py-3">{r.alignment ? <span className={ALIGN_META[r.alignment]?.badge}>{ALIGN_META[r.alignment]?.dot} {ALIGN_META[r.alignment]?.label}</span> : <span className="text-slate-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <DemoDetailModal demoId={openId} readOnly={!mgr} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
